import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: string;
  requiredRoles?: string[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiredRole, requiredRoles }) => {
  const { isAuthenticated, hasRole, hasEmployerProfile, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    const returnTo = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?returnTo=${returnTo}`} replace />;
  }

  // Check single role or any of multiple roles
  if (requiredRole && !hasRole(requiredRole)) {
    return <Navigate to="/" replace />;
  }
  if (requiredRoles && requiredRoles.length > 0 && !requiredRoles.some(r => hasRole(r))) {
    return <Navigate to="/" replace />;
  }

  // Admin should be explicitly restricted from employer-specific routes unless they are allowed
  if (hasRole('ADMIN') && requiredRole === 'EMPLOYER') {
    return <Navigate to="/admin-dashboard" replace />;
  }

  // Note: Employer profile check is handled by individual pages (EmployerDashboard, etc.)
  // NOT here, to avoid race conditions with async profile loading from the API.

  return <>{children}</>;
};

export default ProtectedRoute;
